import axios from 'axios';

/**
 * Generate a presigned upload URL from backend
 * @param {File} file - The file to upload
 * @param {string} orderToken - The order UUID token
 * @param {string} imageType - Type of image ('original' or 'sample')
 * @returns {Promise<Object>} Presigned URL data including url, object_key, cdn_url
 */
export async function generatePresignedUrl(file, orderToken, imageType = 'original') {
  try {
    const fileExtension = file.name.split('.').pop().toLowerCase();
    
    const response = await axios.post('/images/generate-presigned-upload-url', {
      filename: file.name,
      file_type: fileExtension,
      token: orderToken,
      file_size: file.size,
      image_type: imageType,
    });

    return response.data;
  } catch (error) {
    console.error('Error generating presigned URL:', error);

    const enhancedError = new Error(
      error.response?.data?.message ||
      'Failed to generate upload URL. Please try again.'
    );

    enhancedError.source = {
      status: error.response?.status,
      headers: error.response?.headers,
      data: error.response?.data,
    };

    throw enhancedError;
  }
}

/**
 * Upload file directly to S3 using presigned URL
 * @param {string} presignedUrl - The presigned URL for upload
 * @param {File} file - The file to upload
 * @param {Function} onProgress - Callback for upload progress (0-100)
 * @returns {Promise<void>}
 */
export async function uploadToS3(presignedUrl, file, onProgress) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();

    xhr.upload.addEventListener('progress', (e) => {
      if (e.lengthComputable && onProgress) {
        const percentComplete = (e.loaded / e.total) * 100;
        onProgress(percentComplete);
      }
    });

    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
      } else {
        reject(new Error(`Upload failed with status ${xhr.status}`));
      }
    });

    xhr.addEventListener('error', () => {
      reject(new Error('Network error during upload'));
    });

    xhr.addEventListener('abort', () => {
      reject(new Error('Upload aborted'));
    });

    xhr.open('PUT', presignedUrl);
    xhr.setRequestHeader('Content-Type', file.type || 'application/octet-stream');
    xhr.send(file);
  });
}

/**
 * Confirm upload with backend after successful S3 upload
 * @param {Object} confirmData - Upload confirmation data
 * @param {string} confirmData.token - Order UUID token
 * @param {string} confirmData.object_key - S3 object key
 * @param {string} confirmData.filename - Original filename
 * @param {number} confirmData.file_size - File size in bytes
 * @param {string} confirmData.cdn_url - CDN URL of uploaded file
 * @param {string} confirmData.image_type - Type of image ('original' or 'sample')
 * @returns {Promise<Object>} Image record from backend
 */
export async function confirmUpload(confirmData) {
  try {
    const response = await axios.post('/images/upload-confirm', confirmData);
    return response.data;
  } catch (error) {
    console.error('Error confirming upload:', error);

    const enhancedError = new Error(
      error.response?.data?.message ||
      'Failed to confirm upload. Please contact support.'
    );

    enhancedError.source = {
      status: error.response?.status,
      headers: error.response?.headers,
      data: error.response?.data,
    };
    
    throw enhancedError;
  }
}

/**
 * Complete upload flow: generate presigned URL, upload to S3, confirm
 * @param {File} file - The file to upload
 * @param {string} orderToken - The order UUID token
 * @param {string} imageType - Type of image ('original' or 'sample')
 * @param {Function} onProgress - Callback for upload progress (0-100)
 * @returns {Promise<Object>} Complete image record from backend
 */
export async function uploadFileToCDN(file, orderToken, imageType = 'original', onProgress) {
  try {
    if (onProgress) onProgress(0);
    const presignedData = await generatePresignedUrl(file, orderToken, imageType);

    await uploadToS3(presignedData.presigned_url, file, (progress) => {
      if (onProgress) onProgress(progress);
    });

    const imageRecord = await confirmUpload({
      token: orderToken,
      object_key: presignedData.object_key,
      filename: file.name,
      file_size: file.size,
      cdn_url: presignedData.cdn_url,
      image_type: imageType,
    });

    if (onProgress) onProgress(100);
    return imageRecord;
  } catch (error) {
    console.error('Upload flow error:', error);
    throw error;
  }
}

